// Boot-splash hand-off: native launch screen → web app.
//
// A tenant's splash image (Super Admin → tenant → Splash screen) is baked
// into the native launch screen at build time (scripts/build_app.py). The
// native app then loads THIS shared web bundle from a remote URL, so without
// help the user sees: native splash → blank WebView → "Loading…" → app.
//
// public/index.html closes that gap. Before the React bundle even loads, its
// inline script paints a full-screen overlay with the same image on the same
// background, from the per-tenant config cached here on a PREVIOUS launch —
// so an image change reaches every device without an app rebuild (on the
// launch after the next one). React calls hideBootSplash() once the first
// real screen has rendered.
//
// Rules (mirrored by the inline script — keep them in sync):
//   * cache key per tenant slug: `fd_splash:<slug>` = {url, bg, b, v};
//   * `b` is the build token the native shell stamps into the user agent
//     ("FDSplash/<token>", scripts/build_app.py splash_build_token). Device
//     storage outlives a build — Android Auto Backup restores it on
//     reinstall, iOS restores it from a backup, an upgrade keeps it — so an
//     entry is only ever used by the build that wrote it. A build made with
//     no splash ("none") never paints one, whatever storage holds;
//   * nothing cached → no overlay (first launch = the old behaviour);
//   * cached WITH an image → the background colour paints at once and the
//     artwork replaces it on decode; if the image is slow or its URL is stale
//     (re-upload), the colour stays rather than leaving a blank screen;
//   * only native apps / installed PWAs get the overlay, never a browser tab;
//   * every storage access is best-effort (private mode, quota, disabled
//     storage) and can never break boot.

export const SPLASH_CACHE_PREFIX = "fd_splash:";
/** Cache schema version — bump to invalidate every device's cached entry.
 *  v2: `url` may be "" (tenant has no splash image → background colour only).
 *  v3: `b` — the build token the entry was written under (see the rules). */
export const SPLASH_CACHE_VERSION = 3;
/** User-agent stamp the native build appends: "FDSplash/<token>". Kept in
 *  sync with scripts/build_app.py SPLASH_UA_MARKER and public/index.html. */
export const SPLASH_UA_MARKER = "FDSplash/";
/** The token of a build made with no splash configured. */
export const SPLASH_BUILD_NONE = "none";
export const BOOT_SPLASH_ID = "fd-boot-splash";
export const BOOT_SPLASH_HIDE_CLASS = "fd-boot-splash-hide";
/** Minimum time the overlay stays up once painted, so it never just flashes.
 *  Measured from the moment the ARTWORK is on screen (the inline script sets
 *  `data-shown-at` when it reveals), not from when the element was created. */
export const MIN_VISIBLE_MS = 600;
/** Hard cap the inline script gives the splash image to load + decode before
 *  it gives up and lets the app through. Kept in sync with public/index.html
 *  (bootSplash.test.js asserts the two agree).
 *
 *  Sized for a cold CDN fetch on mobile data: a shorter cap silently skipped
 *  the artwork on exactly the slow connections it matters most on. Giving up
 *  is no longer a blank screen either — the overlay falls back to the
 *  configured background colour — so a generous cap costs nothing. */
export const SPLASH_IMAGE_TIMEOUT_MS = 6000;
/** How long a tenant's CUSTOM splash artwork stays on screen once it is
 *  actually visible, before the app takes over. Change this one number to
 *  change the hold.
 *
 *  It applies ONLY to a tenant with an uploaded splash, and the clock starts
 *  when the artwork is revealed (`data-shown-at`), never at app start. A
 *  tenant with no splash image keeps MIN_VISIBLE_MS, i.e. the app opens as
 *  quickly as it always has. public/index.html's fail-safe is kept longer
 *  than this so it can't cut the hold short. */
export const CUSTOM_SPLASH_MIN_VISIBLE_MS = 4000;
/** Events the inline script fires on the overlay once the artwork resolves. */
export const SPLASH_READY_EVENT = "fd-boot-splash-ready";
export const SPLASH_ABORT_EVENT = "fd-boot-splash-abort";
/** Matches the opacity transition in public/index.html. */
export const FADE_MS = 200;

const HEX_RE = /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const SPLASH_BG_RE = /^#[0-9A-Fa-f]{6}$/;
const HTTP_URL_RE = /^https?:\/\//i;
// scripts/build_app.py's own brand-colour default.
const DEFAULT_BG = "#3A86FF";

export function splashCacheKey(slug) {
  return SPLASH_CACHE_PREFIX + String(slug || "");
}

const UA_TOKEN_RE = /(?:^|\s)FDSplash\/([A-Za-z0-9]+)/;

/** The splash build token of the app shell this page runs in, read from the
 *  user agent — "" in a browser tab, a PWA, or a build that predates the
 *  stamp (there the cache rules stand alone, as before). */
export function splashBuildToken() {
  try {
    const m = UA_TOKEN_RE.exec(String(window.navigator?.userAgent || ""));
    return m ? m[1] : "";
  } catch {
    return "";
  }
}

/** {url, bg, b, v} for a tenant branding payload.
 *
 *  `url` is "" when the tenant has no splash image — the launch screen is
 *  then the background colour alone, matching the native build (which paints
 *  a flat `splash.png`). `bg` resolves exactly like that build:
 *  splash_bg_color when it's a valid #RRGGBB, otherwise the brand colour.
 *  `b` binds the entry to the build that writes it. */
export function splashConfigFromBranding(branding) {
  const b = branding || {};
  const raw = typeof b.splash_url === "string" ? b.splash_url.trim() : "";
  const url = HTTP_URL_RE.test(raw) ? raw : "";
  const own = typeof b.splash_bg_color === "string" ? b.splash_bg_color.trim() : "";
  const primary = typeof b.primary_color === "string" ? b.primary_color.trim() : "";
  const bg = SPLASH_BG_RE.test(own) ? own : HEX_RE.test(primary) ? primary : DEFAULT_BG;
  return { url, bg, b: splashBuildToken(), v: SPLASH_CACHE_VERSION };
}

/** The cached, validated splash config for `slug`, or null — null too for
 *  an entry another build wrote (restored storage, upgrade), exactly as the
 *  inline script treats it. */
export function readSplash(slug) {
  if (!slug) return null;
  try {
    const raw = window.localStorage.getItem(splashCacheKey(slug));
    if (!raw) return null;
    const cfg = JSON.parse(raw);
    if (!cfg || cfg.v !== SPLASH_CACHE_VERSION) return null;
    if (typeof cfg.url !== "string") return null;
    if (cfg.url && !HTTP_URL_RE.test(cfg.url)) return null;      // "" = colour only
    if (typeof cfg.bg !== "string" || !HEX_RE.test(cfg.bg)) return null;
    if (typeof cfg.b !== "string" || cfg.b !== splashBuildToken()) return null;
    return { url: cfg.url, bg: cfg.bg, b: cfg.b, v: cfg.v };
  } catch {
    return null;
  }
}

/** Cache (or clear) the splash for `slug` from the public tenant payload's
 *  branding. Called by AuthProvider whenever that payload arrives; the next
 *  launch paints from it. A tenant without a splash image caches a
 *  colour-only entry (→ the background colour, full screen). Only ever
 *  touches this slug's own key. */
export function rememberSplash(slug, branding) {
  if (!slug) return;
  const key = splashCacheKey(slug);
  const cfg = splashConfigFromBranding(branding);
  try {
    if (!cfg) {
      window.localStorage.removeItem(key);
      return;
    }
    const prev = readSplash(slug);
    if (prev && prev.url === cfg.url && prev.bg === cfg.bg) return;
    window.localStorage.setItem(key, JSON.stringify(cfg));
    if (cfg.url && (!prev || prev.url !== cfg.url)) {
      // Warm the HTTP cache so the next launch can paint it immediately.
      try { new window.Image().src = cfg.url; } catch { /* best-effort */ }
    }
  } catch {
    /* storage unavailable (private mode, quota, disabled) — no overlay next launch */
  }
}

/** Resolves once this tenant's splash artwork can be painted — or at once
 *  when the tenant has none.
 *
 *  For the FIRST launch after an install there is no cached config yet, so
 *  the inline script paints no overlay and there is nothing to hand off to.
 *  The native launch screen is already showing this very artwork, so the
 *  caller uses this to hold it until the image is genuinely ready instead of
 *  dropping it onto a half-painted app. Never rejects, and never waits
 *  longer than SPLASH_IMAGE_TIMEOUT_MS — a slow or dead CDN must not hold up
 *  boot. Resolves true only when the artwork is decodable. */
export function whenSplashImageReady(branding) {
  // A build made without a splash never paints one — nothing to wait for,
  // even if the tenant has since uploaded an image (that needs a rebuild).
  if (splashBuildToken() === SPLASH_BUILD_NONE) return Promise.resolve(false);
  const cfg = splashConfigFromBranding(branding);
  if (!cfg || !cfg.url) return Promise.resolve(false);
  if (typeof window === "undefined" || typeof window.Image !== "function") return Promise.resolve(false);
  return new Promise((resolve) => {
    let settled = false;
    let timer = null;
    const finish = (ready) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      resolve(ready);
    };
    timer = setTimeout(() => finish(false), SPLASH_IMAGE_TIMEOUT_MS);
    try {
      const img = new window.Image();
      img.onload = () => {
        const decoded = img.decode ? img.decode() : Promise.resolve();
        Promise.resolve(decoded).then(() => finish(true), () => finish(true));
      };
      img.onerror = () => finish(false);
      img.src = cfg.url;
    } catch {
      finish(false);                       // no Image in this environment
    }
  });
}


/** Hide the native launch splash (@capacitor/splash-screen). No-op on the
 *  web and on builds without the plugin. */
export function hideNativeSplash() {
  try {
    const p = window.Capacitor?.Plugins?.SplashScreen?.hide?.();
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch {
    /* never let a splash call break the app */
  }
}

/** Remove the boot overlay (short fade, after MIN_VISIBLE_MS on screen) and
 *  hide the native splash. Safe to call any number of times, with or
 *  without an overlay present. */
/** Whether this page runs in a native build made WITH a custom splash: its
 *  artwork has been on screen natively since the app's first frame
 *  (Android: the generated FDSplashScreenPlugin; iOS: the launch storyboard
 *  and the splash plugin), so the splash clock started before this page did. */
function nativeCustomSplashUp() {
  const token = splashBuildToken();
  return !!token && token !== SPLASH_BUILD_NONE;
}

/** Milliseconds since this page could first have painted anything — the
 *  arrival of index.html's first byte (navigation `responseStart`), falling
 *  back to the navigation start. Native artwork is on screen from the app's
 *  first frame, which lands between those two, so counting from here never
 *  under-counts the hold. 0 where unknown (→ the full hold applies). */
function sincePageStart() {
  try {
    if (typeof performance === "undefined" || !(performance.timeOrigin > 0)) return 0;
    const nav = performance.getEntriesByType ? performance.getEntriesByType("navigation")[0] : null;
    const anchor = performance.timeOrigin + (nav && nav.responseStart > 0 ? nav.responseStart : 0);
    return Math.max(0, Date.now() - anchor);
  } catch {
    return 0;
  }
}

export function hideBootSplash() {
  if (typeof document === "undefined") return;
  const el = document.getElementById(BOOT_SPLASH_ID);
  if (!el) {
    // No overlay to swap to (nothing cached yet: the first launch after an
    // install). In a custom-splash build the NATIVE artwork is up, so give
    // it the same configured hold — counted from page start, since it was
    // already showing before that — rather than dropping it the instant
    // React is ready. Any other build hands off at once, as always.
    if (nativeCustomSplashUp()) {
      setTimeout(hideNativeSplash, Math.max(0, CUSTOM_SPLASH_MIN_VISIBLE_MS - sincePageStart()));
    } else {
      hideNativeSplash();
    }
    return;
  }
  // The artwork hasn't decoded yet: the NATIVE splash is still covering the
  // screen with the same image, so leave it there. React being ready is not
  // a reason to tear down a splash the user hasn't seen — dismissing it here
  // is exactly what made the uploaded splash never appear. The inline script
  // resolves this either way (decoded → ready, failed/timed out → abort, which
  // leaves the configured background colour up), and its own fail-safe means
  // we can never wait forever. Both paths come back through here, so the
  // overlay — artwork or colour fallback — is always taken down properly.
  if (el.getAttribute("data-pending") === "1") {
    el.addEventListener(SPLASH_READY_EVENT, () => hideBootSplash(), { once: true });
    el.addEventListener(SPLASH_ABORT_EVENT, () => hideBootSplash(), { once: true });
    return;
  }
  if (el.getAttribute("data-hiding") === "1") return;
  el.setAttribute("data-hiding", "1");
  const shownAt = Number(el.getAttribute("data-shown-at")) || 0;
  // A custom splash is held for its full configured duration. An overlay with
  // no image — the colour fallback the inline script leaves up when the
  // artwork never arrives — keeps the short minimum instead: it exists to
  // avoid a blank screen, not to delay the app.
  const hold = el.querySelector("img") ? CUSTOM_SPLASH_MIN_VISIBLE_MS : MIN_VISIBLE_MS;
  // In a custom-splash build the same artwork has been on screen natively
  // since before this page existed, and the overlay merely continued it —
  // one uninterrupted splash, so the clock runs from page start, not from
  // the overlay's reveal (which would add the whole load time to the hold).
  const since = nativeCustomSplashUp() ? Math.max(Date.now() - shownAt, sincePageStart()) : Date.now() - shownAt;
  const wait = Math.max(0, hold - since);
  setTimeout(() => {
    el.classList.add(BOOT_SPLASH_HIDE_CLASS);
    hideNativeSplash();
    setTimeout(() => el.remove(), FADE_MS);
  }, wait);
}
